import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Car, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle } from '../lib/api';
import ImageUploader from '../components/ImageUploader';

const emptyVehicle = {
  name: '',
  capacity_min: 1,
  capacity_max: 6,
  description: '',
  description_es: '',
  image_url: '',
  images: [],
  active: true
};

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyVehicle);

  const fetchVehicles = async () => {
    try {
      const response = await getVehicles();
      setVehicles(response.data);
    } catch (error) {
      toast.error('Error loading vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const openCreate = () => {
    setFormData(emptyVehicle);
    setEditingVehicle('new');
  };

  const openEdit = (vehicle) => {
    setFormData(vehicle);
    setEditingVehicle(vehicle.id);
  };

  const handleSave = async () => {
    try {
      if (editingVehicle === 'new') {
        await createVehicle(formData);
        toast.success('Vehicle created');
      } else {
        await updateVehicle(editingVehicle, formData);
        toast.success('Vehicle updated');
      }
      setEditingVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Error saving vehicle');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteVehicle(deleteId);
      toast.success('Vehicle deleted');
      setDeleteId(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Error deleting vehicle');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading vehicles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vehicles">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white lg:hidden">Vehicles</h1>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-vehicle-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle
        </Button>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {vehicles.map((vehicle) => (
          <div 
            key={vehicle.id} 
            className={`admin-card overflow-hidden ${!vehicle.active && 'opacity-60'}`}
            data-testid={`vehicle-card-${vehicle.id}`}
          >
            <div className="h-48 relative">
              <img 
                src={vehicle.image_url} 
                alt={vehicle.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                <div>
                  <h3 className="text-xl font-bold text-white">{vehicle.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <Users className="w-4 h-4" />
                    {vehicle.capacity_min}-{vehicle.capacity_max} passengers
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  vehicle.active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {vehicle.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-400 line-clamp-2 mb-4">{vehicle.description}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                  onClick={() => openEdit(vehicle)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setDeleteId(vehicle.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {vehicles.length === 0 && (
        <div className="admin-card p-12 text-center">
          <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No vehicles found</p>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingVehicle} onOpenChange={() => setEditingVehicle(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVehicle === 'new' ? 'Add New Vehicle' : 'Edit Vehicle'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Vehicle Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Toyota Hiace"
                data-testid="vehicle-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Min Passengers</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.capacity_min}
                  onChange={(e) => setFormData({ ...formData, capacity_min: parseInt(e.target.value) || 1 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Max Passengers</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.capacity_max}
                  onChange={(e) => setFormData({ ...formData, capacity_max: parseInt(e.target.value) || 1 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Description (EN)</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Perfect for medium groups..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Description (ES)</label>
              <Textarea
                value={formData.description_es || ''}
                onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Perfecto para grupos medianos..."
              />
            </div>

            <ImageUploader
              images={formData.images || (formData.image_url ? [formData.image_url] : [])}
              onChange={(imgs) => setFormData({ ...formData, images: imgs, image_url: imgs[0] || '' })}
              maxImages={10}
              label="Fotos del Vehículo (hasta 10)"
            />

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Active</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingVehicle(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-vehicle-btn"
            >
              Save Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Vehicle?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
